import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Zap, LogOut, ExternalLink, Trash2, Mail, Phone, Filter, Search } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS_LABELS = {
  new: "Nouveau",
  in_progress: "En cours",
  done: "Traité",
  archived: "Archivé",
};

const STATUS_COLORS = {
  new: "bg-copper text-white",
  in_progress: "bg-amber-100 text-amber-900 border border-amber-300",
  done: "bg-emerald-100 text-emerald-900 border border-emerald-300",
  archived: "bg-slate-200 text-slate-700 border border-slate-300",
};

export default function AdminDashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/quotes");
      setQuotes(data);
    } catch (e) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let r = quotes;
    if (filter !== "all") r = r.filter((q) => q.status === filter);
    if (query.trim()) {
      const q = query.toLowerCase();
      r = r.filter((it) => [it.name, it.email, it.phone, it.service, it.message].filter(Boolean).join(" ").toLowerCase().includes(q));
    }
    return r;
  }, [quotes, filter, query]);

  const counts = useMemo(() => {
    const c = { all: quotes.length, new: 0, in_progress: 0, done: 0, archived: 0 };
    quotes.forEach((q) => { c[q.status] = (c[q.status] || 0) + 1; });
    return c;
  }, [quotes]);

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/quotes/${id}`, { status });
      setQuotes((arr) => arr.map((q) => (q.id === id ? data : q)));
      if (selected?.id === id) setSelected(data);
      toast.success(`Statut mis à jour : ${STATUS_LABELS[status]}`);
    } catch {
      toast.error("Mise à jour impossible");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce devis ?")) return;
    try {
      await api.delete(`/admin/quotes/${id}`);
      setQuotes((arr) => arr.filter((q) => q.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Devis supprimé");
    } catch {
      toast.error("Suppression impossible");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-background flex" data-testid="admin-dashboard">
      <aside className="w-60 border-r border-border bg-white hidden lg:flex flex-col">
        <div className="p-6 border-b border-border">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 bg-graphite text-white flex items-center justify-center">
              <Zap size={16} className="copper" strokeWidth={2.5} />
            </div>
            <div className="leading-tight">
              <div className="font-display font-bold text-sm tracking-tight">AMINE-ELEC</div>
              <div className="overline copper -mt-0.5">Console</div>
            </div>
          </Link>
        </div>
        <nav className="p-4 flex-1">
          <div className="overline text-muted-foreground px-2 mb-2">Sections</div>
          {["all", "new", "in_progress", "done", "archived"].map((k) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              data-testid={`filter-${k}`}
              className={`w-full text-left px-3 py-2 flex items-center justify-between border-l-2 transition-colors ${
                filter === k ? "border-copper bg-secondary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <span className="text-sm">{k === "all" ? "Toutes" : STATUS_LABELS[k]}</span>
              <span className="font-mono text-xs">{counts[k] || 0}</span>
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-border">
          <div className="text-xs text-muted-foreground mb-2 truncate" data-testid="admin-email">{user?.email}</div>
          <button onClick={handleLogout} className="text-sm flex items-center gap-2 hover:copper transition-colors" data-testid="admin-logout">
            <LogOut size={14} /> Déconnexion
          </button>
        </div>
      </aside>

      <main className="flex-1 flex flex-col min-w-0">
        <header className="border-b border-border bg-white/85 backdrop-blur sticky top-0 z-10">
          <div className="px-6 lg:px-10 py-5 flex items-center justify-between gap-4">
            <div>
              <div className="overline copper">Tableau de bord</div>
              <h1 className="font-display font-bold text-2xl tracking-tight mt-1">Demandes de devis</h1>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Rechercher…"
                  className="pl-9 pr-3 py-2 border border-border text-sm w-56 focus:outline-none focus:border-copper"
                  style={{ borderRadius: 2 }}
                  data-testid="admin-search"
                />
              </div>
              <Link to="/" target="_blank" className="btn-ghost-light text-sm py-2 px-4" data-testid="view-site">
                Voir le site <ExternalLink size={14} />
              </Link>
            </div>
          </div>
        </header>

        <div className="flex-1 grid lg:grid-cols-3 gap-0 min-h-0">
          <div className={`lg:col-span-2 overflow-y-auto border-r border-border ${selected ? "hidden lg:block" : ""}`}>
            {loading ? (
              <div className="p-10 text-center text-muted-foreground font-mono text-sm">Chargement…</div>
            ) : filtered.length === 0 ? (
              <div className="p-16 text-center" data-testid="empty-state">
                <Filter size={28} className="mx-auto text-muted-foreground mb-4" />
                <p className="font-display text-xl">Aucune demande</p>
                <p className="text-muted-foreground mt-1">Les nouvelles demandes apparaîtront ici.</p>
              </div>
            ) : (
              <ul>
                {filtered.map((q) => (
                  <li
                    key={q.id}
                    onClick={() => setSelected(q)}
                    className={`border-b border-border p-6 cursor-pointer hover:bg-secondary transition-colors ${
                      selected?.id === q.id ? "bg-secondary" : ""
                    }`}
                    data-testid={`quote-row-${q.id}`}
                  >
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <div>
                        <div className="font-display font-bold text-lg tracking-tight">{q.name}</div>
                        <div className="font-mono text-xs text-muted-foreground mt-1">
                          {new Date(q.created_at).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" })}
                        </div>
                      </div>
                      <span className={`overline px-2 py-1 ${STATUS_COLORS[q.status]}`}>{STATUS_LABELS[q.status]}</span>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-muted-foreground">
                      <div className="flex items-center gap-2 truncate"><Mail size={12} /> {q.email}</div>
                      {q.phone && <div className="flex items-center gap-2"><Phone size={12} /> {q.phone}</div>}
                    </div>
                    {q.service && <div className="mt-2 overline text-foreground/70">{q.service}</div>}
                    {q.message && <p className="mt-3 text-sm text-foreground/80 line-clamp-2">{q.message}</p>}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <aside className={`lg:col-span-1 bg-secondary overflow-y-auto ${selected ? "" : "hidden lg:block"}`}>
            {selected ? (
              <DetailPanel quote={selected} onClose={() => setSelected(null)} onStatus={updateStatus} onDelete={remove} />
            ) : (
              <div className="p-10 text-center text-muted-foreground">
                <div className="overline mb-2">Détails</div>
                <p>Sélectionnez une demande pour afficher les détails.</p>
              </div>
            )}
          </aside>
        </div>
      </main>
    </div>
  );
}

function DetailPanel({ quote, onClose, onStatus, onDelete }) {
  return (
    <div className="p-8" data-testid="quote-detail">
      <button onClick={onClose} className="overline text-muted-foreground lg:hidden mb-4" data-testid="detail-close">
        ← Retour
      </button>
      <div className="overline copper">{STATUS_LABELS[quote.status]}</div>
      <h2 className="font-display font-bold text-3xl tracking-tight mt-2">{quote.name}</h2>
      <div className="font-mono text-xs text-muted-foreground mt-2">
        Reçu le {new Date(quote.created_at).toLocaleString("fr-FR", { dateStyle: "long", timeStyle: "short" })}
      </div>

      <div className="mt-8 space-y-4">
        <a href={`mailto:${quote.email}`} className="block border border-border bg-card p-4 hover:border-foreground transition-colors" data-testid="detail-email">
          <div className="overline text-muted-foreground mb-1">Email</div>
          <div className="font-medium">{quote.email}</div>
        </a>
        {quote.phone && (
          <a href={`tel:${quote.phone}`} className="block border border-border bg-card p-4 hover:border-foreground transition-colors" data-testid="detail-phone">
            <div className="overline text-muted-foreground mb-1">Téléphone</div>
            <div className="font-medium">{quote.phone}</div>
          </a>
        )}
        {quote.service && (
          <div className="border border-border bg-card p-4">
            <div className="overline text-muted-foreground mb-1">Prestation</div>
            <div className="font-medium">{quote.service}</div>
          </div>
        )}
        {quote.message && (
          <div className="border border-border bg-card p-4">
            <div className="overline text-muted-foreground mb-2">Message</div>
            <p className="text-sm leading-relaxed whitespace-pre-wrap">{quote.message}</p>
          </div>
        )}
      </div>

      <div className="mt-8 border-t border-border pt-6">
        <div className="overline text-muted-foreground mb-3">Changer le statut</div>
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(STATUS_LABELS).map(([key, label]) => (
            <button
              key={key}
              onClick={() => onStatus(quote.id, key)}
              disabled={quote.status === key}
              className={`px-3 py-2 text-sm border transition-colors ${
                quote.status === key ? "bg-graphite text-white border-graphite" : "border-border hover:border-foreground"
              }`}
              data-testid={`status-btn-${key}`}
            >
              {label}
            </button>
          ))}
        </div>
        <button
          onClick={() => onDelete(quote.id)}
          className="mt-6 inline-flex items-center gap-2 text-sm text-red-700 hover:text-red-900 tech-link"
          data-testid="detail-delete"
        >
          <Trash2 size={14} /> Supprimer définitivement
        </button>
      </div>
    </div>
  );
}
